import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import {
    SearchType,
    type Element as SelectElement,
} from "src/components/generics/nestedSelect/NestedSelect";
import { ReservedId } from "src/constants";
import { useTranslation } from "src/design-system/core/TranslationContext";
import useAuth from "src/hooks/useAuth";
import useSettings from "src/hooks/useSettings";
import { SHARED_LAYOUT_NAMESPACE, TOP_BAR_TK } from "src/layouts/SharedLayout/config";
import { useSuperAdminTopBar } from "src/layouts/SharedLayout/TopBar/TopBarSelect/TopBarSuperAdmin.hook";
import { PATH_POS_LIST, PATH_POS_PARAMETER, PATH_TEST_OFFER_CREATE } from "src/routesConstants";
import type { Id } from "src/types/id";
import { UserRoleEnum } from "src/types/role";
import { PATH_ORGANIZATION } from "src/views/organization/routes";
import { PATH_VISIBILITY } from "src/views/ranking/routes";

interface TopBarSelectHook {
    selected: SelectElement;
    elements: SelectElement[];
    handleSelect: (element: SelectElement) => void;
}

export const useTopBarSelect = (): TopBarSelectHook => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation(`${SHARED_LAYOUT_NAMESPACE}:${TOP_BAR_TK}`);
    const currentPath = location.pathname;

    const { user } = useAuth();
    const poss = user.poss;

    const { settings, saveSettings } = useSettings();

    const setNewPos = (id: Id) => {
        const pos = poss.find((e) => e.id === id);
        let newSelectedElement;
        if (id === ReservedId.ORGANIZATION_ID) {
            newSelectedElement = organisationElement;
        } else if (id === ReservedId.ALL_POSS_ID) {
            newSelectedElement = allPosElement;
        } else if (!isNotSuperAdmin) {
            newSelectedElement = { label: selected.label, value: selected.value };
        } else if (!pos) {
            newSelectedElement = allPosElement;
        } else {
            newSelectedElement = { label: pos.name, value: pos.id };
        }

        const possIds = id === ReservedId.ALL_POSS_ID ? [] : [id];

        saveSettings({
            selectedElement: newSelectedElement,
            offerHeaderFilters: {
                ...settings.offerHeaderFilters,
                poss: [],
            },
            sponsoredAdHeaderFilters: {
                ...settings.sponsoredAdHeaderFilters,

                poss: [],
            },
            possIds,
        });
    };

    const organizationView = currentPath.startsWith(PATH_ORGANIZATION);
    const posListView = currentPath.startsWith(PATH_POS_LIST);
    const parameterView = currentPath.startsWith(PATH_POS_PARAMETER);
    const allSelectable = !organizationView;

    const isNotSuperAdmin = user.role !== UserRoleEnum.SUPERADMIN;

    const hasPoss = user.poss && user.poss.length > 0;
    const hasOnlyOnePos = user.poss && user.poss.length === 1;
    const noPosSettings = user.poss && !settings.possIds;
    const allowGroups = !organizationView && user.posGroups && user.posGroups.length > 0;

    const organisationElement = {
        label: t("top-bar-select.organization"),
        value: ReservedId.ORGANIZATION_ID,
        disabled: true,
        type: SearchType.Organization,
    };
    const allPosElement = {
        label: t("top-bar-select.all"),
        value: ReservedId.ALL_POSS_ID,
        type: SearchType.PointOfSalesGroup,
    };

    let rawSelected: SelectElement;
    if (organizationView) {
        rawSelected = organisationElement;
    } else if (settings.selectedElement) {
        if (settings.selectedElement.value === ReservedId.ALL_POSS_ID) {
            rawSelected = allPosElement;
            // Group/organization selection — keep nested element as-is
        } else if (settings.selectedElement.nested) {
            rawSelected = settings.selectedElement;
            // We check if the selected pos is still in the available poss
        } else if (poss.some((p) => p.id === settings.selectedElement.value)) {
            rawSelected = settings.selectedElement;
        } else {
            // If the pos is not available force the selection of all poss
            rawSelected = allPosElement;
            setNewPos(ReservedId.ALL_POSS_ID);
        }
    } else if (hasOnlyOnePos) {
        rawSelected = {
            label: user.poss[0].name,
            value: user.poss[0].id,
        };
    } else {
        rawSelected = allPosElement;
    }

    let elements: SelectElement[] = [
        ...poss.map((e) => {
            return { label: e.name, value: e.id };
        }),
    ];
    const selected = rawSelected.nested
        ? rawSelected
        : (elements.find((el) => el.value === rawSelected.value) ?? rawSelected);

    const setNewPoss = (element: SelectElement) => {
        const poss = element.nested ? element.nested.map((e) => e.value) : [element.value];

        const possIds = poss[0] === ReservedId.ALL_POSS_ID ? [] : poss;

        saveSettings({
            selectedElement: element,
            offerHeaderFilters: {
                ...settings.offerHeaderFilters,
                poss: [],
            },
            sponsoredAdHeaderFilters: {
                ...settings.sponsoredAdHeaderFilters,
                poss: [],
            },
            possIds,
        });
    };

    const handleSelect = (element: SelectElement) => {
        const isSinglePos = !element.nested && element.value > 0;
        let elem = element;

        if (element.type === SearchType?.Organization) {
            const poss = [];
            for (const nest of element.nested) {
                for (const pos of nest.nested) {
                    poss.push(pos);
                }
            }

            elem = {
                label: element.label,
                type: element.type,
                value: element.value,
                nested: poss,
            };
        }

        setNewPoss(elem);
        if ((organizationView || posListView) && isSinglePos) {
            history.push(`${PATH_POS_PARAMETER}/details`);
        }
        if (parameterView && !isSinglePos) {
            history.push(`${PATH_POS_LIST}/poss`);
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const queryPosId = Number(query.get("posId"));

        if (currentPath.startsWith(PATH_POS_PARAMETER)) {
            if (queryPosId) {
                setNewPos(queryPosId);
            } else {
                if (!settings.possIds && hasPoss) {
                    setNewPos(user.poss[0].id);
                }
            }
        }

        if (currentPath.startsWith(PATH_TEST_OFFER_CREATE)) {
            if (queryPosId) {
                setNewPos(queryPosId);
            }
        }

        if (currentPath.startsWith(PATH_VISIBILITY) && hasOnlyOnePos && hasPoss) {
            setNewPos(user.poss[0].id);
        }
    }, [location, poss]);

    useEffect(() => {
        if (isNotSuperAdmin && hasPoss) {
            if (user.poss.length === 1) {
                setNewPos(user.poss[0].id);
            } else if (noPosSettings) {
                setNewPos(ReservedId.ALL_POSS_ID);
            }
        }
    }, []);

    if (allowGroups) {
        const singleElements = elements;
        const allGroupsPoss = user.posGroups.reduce(
            (possIds: Id[], current) => [...possIds, ...current.poss.map((e) => e.id)],
            []
        );

        elements = user.posGroups.map((posGroup) => ({
            label: posGroup.name,
            value: posGroup.id,
            type: SearchType.PointOfSalesGroup,
            nested: posGroup.poss.map((e) => {
                return { label: e.name, value: e.id, currentGroupId: posGroup.id };
            }),
        }));
        singleElements.forEach((singleElement) => {
            if (!allGroupsPoss.includes(singleElement.value)) {
                elements.push(singleElement);
            }
        });
    }
    if (organizationView) {
        elements = [organisationElement, ...elements];
    }
    if (allSelectable && isNotSuperAdmin && elements.length > 1) {
        elements = [allPosElement, ...elements];
    }

    if (!isNotSuperAdmin) {
        const { elements: superAdminElements } = useSuperAdminTopBar();
        const posGroups = superAdminElements.map((posGroup) => ({
            label: posGroup.name,
            value: posGroup.id,
            type: SearchType.PointOfSalesGroup,
            nested: posGroup.poss.map((pos) => ({
                label: pos.name,
                value: pos.id,
                type: SearchType.PointOfSales,
            })),
            organizationId: posGroup.organization.id,
        }));
        const orgas = superAdminElements.map((orga) => ({
            label: orga.organization.name,
            value: orga.organization.id,
            type: SearchType.Organization,
            nested: posGroups.filter((e) => e.organizationId === orga.organization.id),
        }));
        elements = [allPosElement, ...orgas];
    }

    return {
        selected,
        elements,
        handleSelect,
    };
};
